"use client";

import { useLinkStatus } from "next/link";

/**
 * Soft-nav feedback for a blog link (TRA-333). Render as a child anywhere
 * inside a next/link `<Link>` — useLinkStatus reads the pending state of the
 * nearest enclosing Link, so this works whether that Link is a raw
 * `next/link` (PostCard, TagFilter) or one rendered internally by
 * components/ui/Button (BlogPagination).
 *
 * Exists because app/(marketing)/blog/loading.tsx was removed: that
 * Suspense boundary froze the previous page during a Django round-trip, but
 * it also silently downgraded notFound() responses to HTTP 200 (see
 * AGENTS.md's "Suspense vs notFound()" note) — this replaces its soft-nav
 * feedback with a per-link one that doesn't introduce a boundary.
 */
export function LinkPendingIndicator({
  className = "ml-2",
  wrapperClassName,
}: {
  className?: string;
  /**
   * Optional backdrop chip around the spinner, e.g. for PostCard where it
   * sits over a hero image and needs an opaque backing to stay legible
   * against an arbitrary photo instead of a bare ring in `currentColor`.
   * Applied to a wrapping <span> — kept separate from `className` (which
   * stays on the fixed-size spinner ring itself) so padding here doesn't
   * shrink the ring's own box.
   */
  wrapperClassName?: string;
}) {
  const { pending } = useLinkStatus();
  if (!pending) return null;

  const spinner = (
    <span
      aria-hidden="true"
      className={`inline-block size-3 shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent align-[-1px] ${className}`}
    />
  );

  if (!wrapperClassName) return spinner;
  return <span className={wrapperClassName}>{spinner}</span>;
}
